import React, { createContext, useContext, useState, useCallback } from 'react';
import ReauthDialog, { type ReauthReason } from '@/components/ReauthDialog';

interface ReauthContextType {
  /**
   * Trigger the Google reauth popup dialog.
   * Pass the parsed reauth error body (with googleAccountId & googleAccountEmail).
   * Optionally pass a callback to retry after successful reauth.
   *
   * `reason` selects the dialog copy and defaults to 'expired' (Finding R2-3). Callers
   * that know the grant never covered Google Shopping must pass 'scope_missing',
   * otherwise the user is told the connection expired and to click straight through
   * Google's screen, which is the exact behaviour that caused the missing scope.
   */
  triggerReauth: (
    reauthError: {
      googleAccountId: string;
      googleAccountEmail?: string;
      message?: string;
      reason?: ReauthReason;
    },
    onSuccess?: () => void,
  ) => void;
}

const ReauthContext = createContext<ReauthContextType | null>(null);

export const useReauth = (): ReauthContextType => {
  const ctx = useContext(ReauthContext);
  if (!ctx) throw new Error('useReauth must be used within ReauthProvider');
  return ctx;
};

export const ReauthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{
    open: boolean;
    googleAccountId: string;
    googleAccountEmail?: string;
    reason?: ReauthReason;
    onSuccess?: () => void;
  }>({ open: false, googleAccountId: '' });

  const triggerReauth = useCallback(
    (
      reauthError: { googleAccountId: string; googleAccountEmail?: string; reason?: ReauthReason },
      onSuccess?: () => void,
    ) => {
      setState({
        open: true,
        googleAccountId: reauthError.googleAccountId,
        googleAccountEmail: reauthError.googleAccountEmail,
        reason: reauthError.reason,
        onSuccess,
      });
    },
    []
  );

  const handleClose = useCallback(() => {
    setState({ open: false, googleAccountId: '' });
  }, []);

  const handleSuccess = useCallback(() => {
    state.onSuccess?.();
    setState({ open: false, googleAccountId: '' });
  }, [state.onSuccess]);

  return (
    <ReauthContext.Provider value={{ triggerReauth }}>
      {children}
      {state.open && state.googleAccountId && (
        <ReauthDialog
          open={state.open}
          onClose={handleClose}
          onSuccess={handleSuccess}
          googleAccountId={state.googleAccountId}
          googleAccountEmail={state.googleAccountEmail}
          reason={state.reason}
        />
      )}
    </ReauthContext.Provider>
  );
};
